import { styled } from '@linaria/react';
import {
  Background,
  BackgroundVariant,
  Controls,
  EdgeTypes,
  NodeTypes,
  ReactFlow,
  Panel as ReactFlowPanel,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng } from 'html-to-image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMindMap } from '../../../api/strategy-client/strategy-client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setActivePanel } from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { baseWhite, inkLight } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { StrategyHeader, StrategyTabsList } from '../strategy-header';
import { StrategyTab } from '../strategy.type';
import { AnimatedSvgEdge } from './components/animated-svg-edge';
import { MindMapFilters, type DeliveryProgressFilter } from './mind-map-filters';
import { calculateHierarchicalLayout } from './mind-map.helpers';
import { MindMapNode } from './nodes/mind-map-node';

const nodeTypes: NodeTypes = {
  mindMapNode: MindMapNode,
};

const edgeTypes: EdgeTypes = {
  animatedSvgEdge: AnimatedSvgEdge,
};

function StrategyMindMapInner() {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activePanel = useStrategyStore((state) => state.activePanel);
  const { graph, query } = useMindMap(portfolio?.id);
  const { fitView } = useReactFlow();
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
  const hasInitialized = useRef(false);
  const previousPortfolioId = useRef<string | undefined>(undefined);
  const [deliveryProgressFilter, setDeliveryProgressFilter] = useState<DeliveryProgressFilter>({
    notStarted: true,
    inProgress: true,
    nearComplete: true,
    complete: true,
  });

  // Calculate base nodes and edges from graph
  const { baseNodes, baseEdges } = useMemo(() => {
    if (!graph || !graph.nodes || !graph.edges) {
      return { baseNodes: [], baseEdges: [] };
    }

    const layout = calculateHierarchicalLayout(graph.nodes, graph.edges, collapsedNodes, t);

    // Use the edges directly without animation
    return { baseNodes: layout.nodes, baseEdges: layout.edges };
  }, [graph, collapsedNodes, t]);

  // Reset initialization when portfolio changes
  useEffect(() => {
    if (portfolio?.id !== previousPortfolioId.current) {
      previousPortfolioId.current = portfolio?.id;
      hasInitialized.current = false;
      setCollapsedNodes(new Set());
    }
  }, [portfolio?.id]);

  // Collapse all strategic goals, outcomes, and placeholders by default when base nodes are ready
  useEffect(() => {
    if (baseNodes.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      const initialCollapsed = new Set<string>();
      baseNodes.forEach((node) => {
        if (
          node.data.nodeType === 'goal' ||
          node.data.nodeType === 'corporate-goal' ||
          node.data.nodeType === 'outcome' ||
          node.data.nodeType === 'no-goals' ||
          node.data.nodeType === 'no-objectives'
        ) {
          initialCollapsed.add(node.id);
        }
      });
      setCollapsedNodes(initialCollapsed);
    }
  }, [baseNodes]);

  const handleToggleCollapse = useCallback((nodeId: string) => {
    setCollapsedNodes((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Re-fit view when nodes are collapsed/expanded or filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({
        padding: 0.25,
        duration: 300,
        minZoom: 0.4,
        maxZoom: 1,
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [fitView]);

  const onDownloadImage = useCallback(() => {
    const nodeElement = document.querySelector('.react-flow') as HTMLElement;
    if (!nodeElement) return;

    toPng(nodeElement, {
      filter: (node) => {
        // Exclude controls, background, and attribution
        const classList = node.classList;
        if (!classList) return true;
        return (
          !classList.contains('react-flow__controls') &&
          !classList.contains('react-flow__background') &&
          !classList.contains('react-flow__attribution')
        );
      },
      backgroundColor: '#ffffff',
    })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `strategy-mind-map-${portfolio?.name || 'export'}-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Failed to export image:', err);
      });
  }, [portfolio]);

  // Helper function to check if node matches delivery progress filter
  const matchesDeliveryProgressFilter = useCallback(
    (progress: number) => {
      if (progress === 0 && deliveryProgressFilter.notStarted) return true;
      if (progress > 0 && progress <= 74 && deliveryProgressFilter.inProgress) return true;
      if (progress >= 75 && progress < 100 && deliveryProgressFilter.nearComplete) return true;
      if (progress === 100 && deliveryProgressFilter.complete) return true;
      return false;
    },
    [deliveryProgressFilter],
  );

  // Filter nodes and edges based on collapsed state and delivery progress
  const { nodes, edges } = useMemo(() => {
    // First, apply delivery progress filter
    const progressFilteredNodes = baseNodes.filter((node) => {
      // Don't filter portfolio, placeholder nodes
      if (
        node.data.nodeType === 'portfolio' ||
        node.data.nodeType === 'no-goals' ||
        node.data.nodeType === 'no-objectives'
      ) {
        return true;
      }

      // Filter based on progress
      const progress = (node.data.meta as any)?.progress || 0;
      return matchesDeliveryProgressFilter(progress);
    });

    const progressFilteredNodeIds = new Set(progressFilteredNodes.map((n) => n.id));

    // Build maps of parent-child relationships from ALL edges (not just filtered)
    const childrenMap = new Map<string, string[]>();
    const parentMap = new Map<string, string[]>();
    for (const edge of baseEdges) {
      // Children map
      if (!childrenMap.has(edge.source)) {
        childrenMap.set(edge.source, []);
      }
      childrenMap.get(edge.source)!.push(edge.target);

      // Parent map
      if (!parentMap.has(edge.target)) {
        parentMap.set(edge.target, []);
      }
      parentMap.get(edge.target)!.push(edge.source);
    }

    // Find all nodes that should be hidden
    const hiddenNodes = new Set<string>();

    // Function to recursively hide all descendants of a node
    const hideDescendants = (nodeId: string) => {
      const children = childrenMap.get(nodeId) || [];
      for (const childId of children) {
        if (!hiddenNodes.has(childId)) {
          hiddenNodes.add(childId);
          // Recursively hide all descendants
          hideDescendants(childId);
        }
      }
    };

    // Step 1: Hide all descendants of collapsed nodes (process ALL base nodes, not just filtered)
    for (const nodeId of collapsedNodes) {
      // Hide children of any collapsed node, even if the collapsed node itself is hidden
      const nodeExists = baseNodes.some((n) => n.id === nodeId);
      if (nodeExists) {
        hideDescendants(nodeId);
      }
    }

    // Step 2: Hide nodes that don't have a visible AND expanded parent (orphans)
    // Only the portfolio node should have no parent
    for (const node of progressFilteredNodes) {
      if (node.data.nodeType === 'portfolio') {
        continue; // Portfolio is root, always visible
      }

      const parents = parentMap.get(node.id) || [];
      const hasVisibleExpandedParent = parents.some((parentId) => {
        // A parent is visible AND expanded if:
        // 1. It passed the progress filter
        // 2. It's not hidden by collapse
        // 3. It's not collapsed itself (otherwise its children should be hidden)
        return progressFilteredNodeIds.has(parentId) && !hiddenNodes.has(parentId) && !collapsedNodes.has(parentId);
      });

      if (!hasVisibleExpandedParent) {
        hiddenNodes.add(node.id);
        // Also hide all its descendants
        hideDescendants(node.id);
      }
    }

    // Step 3: Ensure children of expanded outcomes are visible
    // If an outcome is visible and expanded, its immediate initiatives should be shown.
    for (const node of progressFilteredNodes) {
      const isOutcome = node.data.nodeType === 'outcome';
      const isVisibleOutcome = isOutcome && !collapsedNodes.has(node.id) && !hiddenNodes.has(node.id);
      if (!isVisibleOutcome) {
        continue;
      }

      const childIds = childrenMap.get(node.id) || [];
      for (const childId of childIds) {
        // Unhide the initiative if it is part of the filtered set and not explicitly filtered out by progress
        if (progressFilteredNodeIds.has(childId)) {
          hiddenNodes.delete(childId);
        }
      }
    }

    // Add collapse handlers and filter hidden nodes

    const visibleNodes: Node[] = progressFilteredNodes
      .filter((node) => {
        const isVisible = !hiddenNodes.has(node.id);
        return isVisible;
      })
      .map((node) => ({
        ...node,
        data: {
          ...node.data,
          isCollapsed: collapsedNodes.has(node.id),
          onToggleCollapse: () => handleToggleCollapse(node.id),
        },
      }));

    // Filter edges - only show edges where both source and target are visible
    const visibleNodeIds = new Set(visibleNodes.map((n) => n.id));
    const visibleEdges: Edge[] = baseEdges.filter((edge) => {
      return visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target);
    });

    return { nodes: visibleNodes, edges: visibleEdges };
  }, [baseNodes, baseEdges, collapsedNodes, handleToggleCollapse, matchesDeliveryProgressFilter]);

  const handleSetFilterShow = (show: boolean) => {
    setActivePanel(show ? Panel.Filters : null);
  };

  const mindMapFlow = (
    <Container>
      {query.isLoading && <BrandedLoadingOverlay visible={true} variant="colored" />}
      {!portfolio && (
        <EmptyState>
          <EmptyStateText>Please select a portfolio to view the mind map.</EmptyStateText>
        </EmptyState>
      )}
      {portfolio && query.isError && (
        <EmptyState>
          <EmptyStateText>Error loading mind map data. Please try again.</EmptyStateText>
        </EmptyState>
      )}
      {portfolio && !query.isLoading && !query.isError && (!graph || nodes.length === 0) && (
        <EmptyState>
          <EmptyStateText>No data available for mind map visualization.</EmptyStateText>
        </EmptyState>
      )}
      {portfolio && !query.isLoading && !query.isError && graph && nodes.length > 0 && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{
            padding: 0.25,
            minZoom: 0.4,
            maxZoom: 1,
          }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.65 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
          panOnScroll={true}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1.5} color="#e3e5e5" />
          <Controls showInteractive={false} />
          <ReactFlowPanel position="top-right">
            <ExportButton
              onClick={onDownloadImage}
              leftSection={<Icon name="download" size={16} color={baseWhite} />}
              variant="secondary"
              size="xs"
              radius="md"
            >
              Export
            </ExportButton>
          </ReactFlowPanel>
        </ReactFlow>
      )}
    </Container>
  );

  return (
    <MenuDrivenContainer header={<StrategyHeader />}>
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={<StrategyTabsList active={StrategyTab.MindMap} />}
        sideFilters={{
          title: 'Filters',
          content: (
            <MindMapFilters
              setShow={handleSetFilterShow}
              title="Filters"
              deliveryProgressFilter={deliveryProgressFilter}
              onDeliveryProgressFilterChange={setDeliveryProgressFilter}
            />
          ),
          show: activePanel === Panel.Filters,
          setShow: handleSetFilterShow,
        }}
        mainSection={mindMapFlow}
        showSideSection={false}
      />
    </MenuDrivenContainer>
  );
}

export function StrategyMindMap() {
  useDocumentTitle('Mind Map - Bloomfilter');

  return (
    <ReactFlowProvider>
      <StrategyMindMapInner />
    </ReactFlowProvider>
  );
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  background: ${baseWhite};
  position: relative;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const EmptyStateText = styled.div`
  color: ${inkLight};
  font-size: 16px;
`;

const ExportButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
`;
